import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { apiRequest } from "../utils/http";

const initialAdminForm = {
  fullName: "",
  email: "",
  phone: "",
  password: "",
  role: "staff"
};

const initialEditForm = {
  id: "",
  fullName: "",
  email: "",
  phone: "",
  role: "staff",
  status: "active"
};

const AdminsPage = () => {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState(initialAdminForm);
  const [editForm, setEditForm] = useState(initialEditForm);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetPasswordTarget, setResetPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const loadAdmins = async () => {
    try {
      const response = await apiRequest("/api/admin/users");
      setAdmins((response?.data || []).filter((user) => ["super_admin", "staff", "support_admin", "stockkeeper", "business_manager"].includes(user.role)));
    } catch (loadError) {
      setError(loadError.message || "Unable to load admins.");
    }
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((current) => ({ ...current, [name]: value }));
  };

  const handleCreateAdmin = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    try {
      setIsSubmitting(true);
      const response = await apiRequest("/api/admin/admins", {
        method: "POST",
        body: JSON.stringify(form)
      });
      setSuccess(response?.message || "Admin created successfully.");
      setForm(initialAdminForm);
      await loadAdmins();
    } catch (createError) {
      setError(createError.message || "Unable to create admin.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditDialog = (admin) => {
    setEditingAdmin(admin);
    setEditForm({
      id: admin.id,
      fullName: admin.fullName || "",
      email: admin.email || "",
      phone: admin.phone || "",
      role: admin.role || "staff",
      status: admin.status || "active"
    });
  };

  const handleSaveAdmin = async () => {
    setError("");
    setSuccess("");

    try {
      const response = await apiRequest(`/api/admin/users/${editForm.id}`, {
        method: "PATCH",
        body: JSON.stringify(editForm)
      });
      setSuccess(response?.message || "Admin updated successfully.");
      setEditingAdmin(null);
      await loadAdmins();
    } catch (saveError) {
      setError(saveError.message || "Unable to update admin.");
    }
  };

  const handleSuspend = async (admin) => {
    setError("");
    setSuccess("");

    try {
      const response = await apiRequest(`/api/admin/users/${admin.id}/suspend`, {
        method: "PATCH",
        body: JSON.stringify({
          reason: admin.status === "active" ? "Suspended by super admin" : ""
        })
      });
      setSuccess(response?.message || "Admin status updated.");
      await loadAdmins();
    } catch (suspendError) {
      setError(suspendError.message || "Unable to update admin status.");
    }
  };

  const handleDelete = async (admin) => {
    const confirmed = window.confirm(`Delete ${admin.fullName || admin.email}?`);

    if (!confirmed) {
      return;
    }

    setError("");
    setSuccess("");

    try {
      const response = await apiRequest(`/api/admin/users/${admin.id}`, {
        method: "DELETE"
      });
      setSuccess(response?.message || "Admin deleted successfully.");
      await loadAdmins();
    } catch (deleteError) {
      setError(deleteError.message || "Unable to delete admin.");
    }
  };

  const handleResetPassword = async () => {
    setError("");
    setSuccess("");

    try {
      const response = await apiRequest(`/api/admin/users/${resetPasswordTarget.id}/password`, {
        method: "PATCH",
        body: JSON.stringify({ newPassword })
      });
      setSuccess(response?.message || "Password updated successfully.");
      setResetPasswordTarget(null);
      setNewPassword("");
    } catch (resetError) {
      setError(resetError.message || "Unable to reset password.");
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" sx={{ mb: 1 }}>Admins</Typography>
        <Typography sx={{ color: "text.secondary" }}>
          Super admins can create, edit, suspend, delete, and reassign roles for all admin users.
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={5}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Create Admin User</Typography>
            <Box component="form" onSubmit={handleCreateAdmin}>
              <Stack spacing={2.5}>
                <TextField label="Full Name" name="fullName" value={form.fullName} onChange={handleChange} fullWidth />
                <TextField label="Email" name="email" value={form.email} onChange={handleChange} fullWidth />
                <TextField label="Phone" name="phone" value={form.phone} onChange={handleChange} fullWidth />
                <TextField select label="Role" name="role" value={form.role} onChange={handleChange} fullWidth>
                  <MenuItem value="staff">Staff</MenuItem>
                  <MenuItem value="support_admin">Support Admin</MenuItem>
                  <MenuItem value="stockkeeper">Stockkeeper</MenuItem>
                  <MenuItem value="business_manager">Business Manager</MenuItem>
                  <MenuItem value="super_admin">Super Admin</MenuItem>
                </TextField>
                <TextField
                  label="Password"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  fullWidth
                />
                <Button type="submit" variant="contained" disabled={isSubmitting}>
                  {isSubmitting ? "Creating..." : "Create Admin"}
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Grid container spacing={3}>
            {admins.map((admin) => (
              <Grid item xs={12} md={6} key={admin.id}>
                <Paper sx={{ p: 3, height: "100%" }}>
                  <Stack spacing={1.25}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6">{admin.fullName}</Typography>
                      <Chip
                        label={admin.status}
                        color={admin.status === "active" ? "success" : "warning"}
                        sx={{ textTransform: "capitalize" }}
                      />
                    </Stack>
                    <Chip label={admin.role.replace("_", " ")} sx={{ width: "fit-content", textTransform: "capitalize" }} />
                    <Typography sx={{ color: "text.secondary" }}>{admin.email}</Typography>
                    <Typography sx={{ color: "text.secondary" }}>{admin.phone || "No phone number"}</Typography>
                    <Typography sx={{ color: "text.secondary" }}>
                      Created {new Date(admin.createdAt).toLocaleDateString()}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 1 }}>
                      <Button variant="outlined" onClick={() => openEditDialog(admin)}>Edit</Button>
                      <Button variant="contained" color="warning" onClick={() => handleSuspend(admin)}>
                        {admin.status === "active" ? "Suspend" : "Reactivate"}
                      </Button>
                      <Button variant="text" color="error" onClick={() => handleDelete(admin)}>
                        Delete
                      </Button>
                      <Button variant="text" onClick={() => setResetPasswordTarget(admin)}>
                        Reset Password
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      <Dialog open={Boolean(editingAdmin)} onClose={() => setEditingAdmin(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Admin User</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField label="Full Name" name="fullName" value={editForm.fullName} onChange={handleEditChange} fullWidth />
            <TextField label="Email" name="email" value={editForm.email} onChange={handleEditChange} fullWidth />
            <TextField label="Phone" name="phone" value={editForm.phone} onChange={handleEditChange} fullWidth />
            <TextField select label="Role" name="role" value={editForm.role} onChange={handleEditChange} fullWidth>
              <MenuItem value="staff">Staff</MenuItem>
              <MenuItem value="support_admin">Support Admin</MenuItem>
              <MenuItem value="stockkeeper">Stockkeeper</MenuItem>
              <MenuItem value="business_manager">Business Manager</MenuItem>
              <MenuItem value="super_admin">Super Admin</MenuItem>
            </TextField>
            <TextField select label="Status" name="status" value={editForm.status} onChange={handleEditChange} fullWidth>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingAdmin(null)}>Cancel</Button>
          <Button onClick={handleSaveAdmin} variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(resetPasswordTarget)} onClose={() => setResetPasswordTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Reset Admin Password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography sx={{ color: "text.secondary" }}>
              Set a new password for {resetPasswordTarget?.fullName || resetPasswordTarget?.email}.
            </Typography>
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordTarget(null)}>Cancel</Button>
          <Button onClick={handleResetPassword} variant="contained">Update Password</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AdminsPage;
